import { useEffect, useRef, useState } from "react";
import { Button } from "@/design-system/components/react";
import { useStudyObservation } from "../../hooks/useStudyObservation";
import { InstructionsDialog } from "./InstructionsDialog";
import {
  evidenceSides,
  hasLandmark,
  isPositive,
  needsChestAnswer,
  validateAnswers,
  type ObservationAnswer,
  type ObservationAnswers,
  type ObservationDefinition,
  type ObservationValue,
  type StudyObservationResponse,
} from "./studyObservation";

export interface StudyObservationPanelProps {
  projectId: string;
  studyInstanceUid: string;
  caseId: string;
  activeSource: { studyInstanceUid: string; seriesInstanceUid: string } | null;
  seriesLabels: Record<string, string>;
  readOnly?: boolean;
  currentIndex: number;
  totalCases: number;
  hasNext: boolean;
  hasPrevious: boolean;
  onNext: () => void;
  onPrevious: () => void;
  onWorkList: () => void;
}

const labels: Record<ObservationValue, string> = {
  yes: "Yes",
  no: "No",
  both: "Both",
  right: "Right",
  left: "Left",
  neither: "Neither",
  unassessable: "Unable to assess",
};

export function StudyObservationPanel(props: StudyObservationPanelProps) {
  // Case identity unmounts the form so late responses cannot seed another case.
  return (
    <StudyObservationLoader
      key={`${props.projectId}:${props.studyInstanceUid}:${props.caseId}`}
      {...props}
    />
  );
}

function StudyObservationLoader(props: StudyObservationPanelProps) {
  const api = useStudyObservation(props.projectId, props.studyInstanceUid, props.caseId);
  if (api.query.isPending)
    return (
      <div className="p-4 text-sm text-fg-muted" role="status">
        Loading anatomy task…
      </div>
    );
  if (api.query.isError || !api.query.data)
    return (
      <div className="p-4 text-sm text-fg" role="alert">
        <p>The anatomy task could not be loaded. No answers were changed.</p>
        <p className="mt-2 text-fg-muted">{api.query.error?.message}</p>
        <Button className="mt-3" variant="outline" onClick={() => void api.query.refetch()}>
          Retry
        </Button>
      </div>
    );
  return <ObservationForm {...props} api={api} initial={api.query.data} />;
}

function ObservationForm({
  api,
  initial,
  ...props
}: StudyObservationPanelProps & {
  api: ReturnType<typeof useStudyObservation>;
  initial: StudyObservationResponse;
}) {
  const [source] = useState(initial);
  const [answers, setAnswers] = useState<ObservationAnswers>(initial.record?.observations ?? {});
  const [chest, setChest] = useState<"yes" | "no" | "unassessable" | undefined>(
    initial.record?.chest_presence_basis === "direct" ? initial.record.chest_presence : undefined,
  );
  const [note, setNote] = useState(initial.record?.note ?? "");
  const [chestSeries, setChestSeries] = useState(initial.record?.chest_presence_series_uid);
  const [record, setRecord] = useState(initial.record);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const taskChanged = initial.task_hash !== source.task_hash;
  const blocked = props.readOnly || api.mutation.isPending || taskChanged;
  const activeUid =
    props.activeSource?.studyInstanceUid === props.studyInstanceUid &&
    source.eligible_series_uids.includes(props.activeSource.seriesInstanceUid)
      ? props.activeSource.seriesInstanceUid
      : null;
  const complete =
    validateAnswers(answers, source.eligible_series_uids) &&
    (!needsChestAnswer(answers) ||
      (!!chest && (chest !== "yes" || source.eligible_series_uids.includes(chestSeries ?? ""))));
  const count = source.task.observations.filter((q) => answers[q.id]).length;
  function choose(q: ObservationDefinition, value: ObservationValue, knownSide?: "right" | "left") {
    if (blocked || ((isPositive(value) || knownSide) && !activeUid)) return;
    const sides = knownSide ? [knownSide] : evidenceSides(value);
    const answer: ObservationAnswer = { value };
    if (sides.length)
      answer.series_evidence = Object.fromEntries(sides.map((side) => [side, activeUid!]));
    setAnswers((previous) => ({ ...previous, [q.id]: answer }));
    setChest(undefined);
    setDirty(true);
    setError(null);
  }
  async function save() {
    if (blocked || !complete) return;
    setError(null);
    try {
      const result = await api.mutation.mutateAsync({
        task_hash: source.task_hash,
        schema_revision: source.task.schema_revision,
        instruction_revision: source.task.instruction_revision,
        expected_record_revision: record?.record_revision ?? null,
        observations: answers,
        ...(needsChestAnswer(answers) ? { chest_presence: chest } : {}),
        ...(needsChestAnswer(answers) && chest === "yes"
          ? { chest_presence_series_uid: chestSeries }
          : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      if (!mounted.current) return;
      setRecord(result);
      setDirty(false);
      if (props.hasNext) props.onNext();
      else props.onWorkList();
    } catch {
      if (mounted.current)
        setError(
          "Save was not confirmed. Your answers remain here. Reload the study if its task or saved answers changed elsewhere.",
        );
    }
  }
  function evidence(q: ObservationDefinition) {
    const a = answers[q.id];
    if (!a?.series_evidence || !Object.keys(a.series_evidence).length) return null;
    return (
      <details className="mt-2 text-[11px] text-fg-muted">
        <summary className="cursor-pointer">
          {Object.entries(a.series_evidence)
            .map(
              ([side, uid]) =>
                `${side === "present" ? "" : `${labels[side as ObservationValue]}: `}${props.seriesLabels[uid!] ?? "Series"}`,
            )
            .join(" · ")}{" "}
          · change
        </summary>
        {Object.entries(a.series_evidence).map(([side, uid]) => (
          <label className="mt-2 block" key={side}>
            {side === "present"
              ? "Supporting series"
              : `${labels[side as ObservationValue]} series`}
            <select
              className="mt-1 w-full border border-border-strong bg-surface p-1 text-fg"
              aria-label={`${q.label}: ${side} supporting series`}
              disabled={blocked}
              value={uid}
              onChange={(e) => {
                setAnswers((previous) => ({
                  ...previous,
                  [q.id]: {
                    ...a,
                    series_evidence: { ...a.series_evidence, [side]: e.target.value },
                  },
                }));
                setDirty(true);
              }}
            >
              {source.eligible_series_uids.map((s) => (
                <option key={s} value={s}>
                  {props.seriesLabels[s] ?? s}
                </option>
              ))}
            </select>
          </label>
        ))}
      </details>
    );
  }
  return (
    <div
      className="flex h-full flex-col bg-bg text-fg text-sm"
      data-testid="study-observation-panel"
    >
      <header className="shrink-0 border-b border-border p-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-fg-muted">
            Study {props.currentIndex + 1} / {props.totalCases}
          </span>
          <Button
            size="sm"
            variant="ghost"
            disabled={api.mutation.isPending || dirty}
            onClick={props.onWorkList}
          >
            Worklist
          </Button>
        </div>
        <h2 className="text-base font-semibold">{source.task.title}</h2>
        <p className="mt-1 text-xs text-fg-muted">
          One choice per landmark. Supporting series captured automatically.
        </p>
        <Button
          size="sm"
          variant="link"
          className="h-7 px-0"
          onClick={() => setInstructionsOpen(true)}
        >
          Reader instructions
        </Button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-3">
        {taskChanged && (
          <p className="my-3 text-warn" role="alert">
            Task instructions changed. Reload the study before continuing.
          </p>
        )}
        {!activeUid && (
          <p className="my-2 text-xs text-fg-muted" role="status">
            Open a diagnostic series before identifying an included landmark.
          </p>
        )}
        {source.task.observations.map((q) => (
          <section key={q.id} className="border-b border-border py-3" aria-label={q.label}>
            <h3 className="font-medium">{q.label}</h3>
            <p className="mt-1 text-xs leading-relaxed text-fg-muted">{q.instructions}</p>
            <div className="mt-2 flex gap-1" role="group" aria-label={q.label}>
              {(q.kind === "bilateral" ? ["both", "right", "left", "neither"] : ["yes", "no"]).map(
                (v) => (
                  <Button
                    key={v}
                    size="sm"
                    variant={answers[q.id]?.value === v ? "default" : "outline"}
                    className="h-8 flex-1 px-2 text-xs"
                    aria-pressed={answers[q.id]?.value === v}
                    disabled={blocked || (isPositive(v as ObservationValue) && !activeUid)}
                    onClick={() => choose(q, v as ObservationValue)}
                  >
                    {labels[v as ObservationValue]}
                  </Button>
                ),
              )}
            </div>
            {answers[q.id]?.value === "unassessable" && (
              <p className="mt-2 text-xs text-fg-muted">
                Unable to assess
                {Object.keys(answers[q.id]?.series_evidence ?? {}).length
                  ? " · included side retained"
                  : ""}
              </p>
            )}
            {evidence(q)}
          </section>
        ))}
        {needsChestAnswer(answers) && (
          <section className="border-b border-border py-3" aria-label="Chest anatomy">
            <h3 className="font-medium">Is any chest anatomy visible?</h3>
            <p className="mt-1 text-xs text-fg-muted">
              Use the chest-tissue definition in the reader instructions.
            </p>
            <div className="mt-2 flex gap-1">
              {(["yes", "no"] as const).map((v) => (
                <Button
                  size="sm"
                  variant={chest === v ? "default" : "outline"}
                  className="h-8 flex-1"
                  key={v}
                  disabled={blocked || (v === "yes" && !activeUid)}
                  aria-pressed={chest === v}
                  onClick={() => {
                    setChest(v);
                    setChestSeries(v === "yes" ? activeUid! : undefined);
                    setDirty(true);
                  }}
                >
                  {labels[v]}
                </Button>
              ))}
            </div>
            {chest === "yes" && (
              <p className="mt-2 text-xs text-fg-muted">
                {props.seriesLabels[chestSeries!] ?? "Supporting series captured"}
              </p>
            )}
            {chest === "unassessable" && (
              <p className="mt-2 text-xs text-fg-muted">Unable to assess</p>
            )}
          </section>
        )}
        <details className="py-3 text-xs">
          <summary className="cursor-pointer text-fg-muted">Unable to assess a landmark?</summary>
          <p className="my-2 text-fg-muted">
            Use only when the images or anatomy prevent a decision. Notes are optional.
          </p>
          {source.task.observations.map((q) => (
            <div key={q.id} className="my-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={answers[q.id]?.value === "unassessable"}
                  disabled={blocked}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const a = answers[q.id];
                      const side =
                        a?.value === "right" || a?.value === "left" ? a.value : undefined;
                      setAnswers((previous) => ({
                        ...previous,
                        [q.id]: {
                          value: "unassessable",
                          ...(side
                            ? { series_evidence: { [side]: a!.series_evidence?.[side] } }
                            : {}),
                        },
                      }));
                    } else
                      setAnswers((previous) => {
                        const next = { ...previous };
                        delete next[q.id];
                        return next;
                      });
                    setChest(undefined);
                    setDirty(true);
                  }}
                />
                {q.label}
              </label>
              {q.kind === "bilateral" && answers[q.id]?.value === "unassessable" && (
                <div className="ml-5 mt-2">
                  <span className="text-fg-muted">Known included side</span>
                  <div className="mt-1 flex gap-1">
                    {([null, "right", "left"] as const).map((side) => (
                      <Button
                        key={side ?? "none"}
                        size="sm"
                        className="h-7 px-2 text-xs"
                        variant={
                          (
                            side
                              ? answers[q.id]?.series_evidence?.[side]
                              : !Object.keys(answers[q.id]?.series_evidence ?? {}).length
                          )
                            ? "default"
                            : "outline"
                        }
                        disabled={blocked || (!!side && !activeUid)}
                        onClick={() => choose(q, "unassessable", side ?? undefined)}
                      >
                        {side ? labels[side] : "None"}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          {needsChestAnswer(answers) && (
            <label className="my-2 flex items-center gap-2">
              <input
                type="checkbox"
                checked={chest === "unassessable"}
                disabled={blocked}
                onChange={(e) => {
                  setChest(e.target.checked ? "unassessable" : undefined);
                  setDirty(true);
                }}
              />
              Chest anatomy
            </label>
          )}
          <label className="mt-3 block">
            Optional note
            <textarea
              className="mt-1 w-full border border-border-strong bg-surface p-2 text-fg"
              maxLength={2000}
              value={note}
              disabled={blocked}
              onChange={(e) => {
                setNote(e.target.value);
                setDirty(true);
              }}
            />
          </label>
        </details>
      </div>
      <footer className="shrink-0 border-t border-border p-3">
        {record && !dirty && (
          <p className="mb-2 text-[11px] text-fg-muted">
            Saved by {record.reader} · {new Date(record.recorded_at).toLocaleString()}
          </p>
        )}
        <p className="mb-2 text-xs text-fg-muted">
          {count} / 5 answered{hasLandmark(answers) ? " · chest anatomy identified" : ""}
        </p>
        {error && (
          <p className="mb-2 text-xs text-warn" role="alert">
            {error}
          </p>
        )}
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={!props.hasPrevious || dirty || api.mutation.isPending}
            onClick={props.onPrevious}
          >
            Previous
          </Button>
          <Button
            size="sm"
            className="flex-1"
            disabled={blocked || !complete}
            onClick={() => void save()}
          >
            {api.mutation.isPending ? "Saving…" : "Save and next"}
          </Button>
        </div>
      </footer>
      <InstructionsDialog
        instructionsKey={null}
        instructionsMarkdown={source.task.instructions}
        title={source.task.title}
        open={instructionsOpen}
        onClose={() => setInstructionsOpen(false)}
      />
    </div>
  );
}
